import { useCallback } from 'react';

/**
 * Enhanced collision detection hook with animated separation
 * Detects overlapping nodes and smoothly animates them apart
 */
export const useCollisionDetection = ({
  nodeRefs,
  minDistance = 20,
  animationDuration = 300
}) => {

  // Helper function to get node bounding box
  const getNodeBounds = useCallback((nodeRef, scale = 1) => {
    if (!nodeRef || !nodeRef.getBoundingClientRect) return null;

    const rect = nodeRef.getBoundingClientRect();
    const containerRect = nodeRef.offsetParent?.getBoundingClientRect();

    if (!containerRect) return null;

    // Get position relative to container and account for scale
    const left = (rect.left - containerRect.left) / scale;
    const top = (rect.top - containerRect.top) / scale;
    const width = rect.width / scale;
    const height = rect.height / scale;

    return {
      left,
      top,
      right: left + width,
      bottom: top + height,
      width,
      height,
      centerX: left + width / 2,
      centerY: top + height / 2
    };
  }, []);

  // Check if two bounding boxes overlap
  const boundingBoxesOverlap = useCallback((bounds1, bounds2, minDistance) => {
    if (!bounds1 || !bounds2) return false;

    // Add minDistance padding to the collision detection
    const padding = minDistance;

    return !(
      bounds1.right + padding < bounds2.left ||
      bounds2.right + padding < bounds1.left ||
      bounds1.bottom + padding < bounds2.top ||
      bounds2.bottom + padding < bounds1.top
    );
  }, []);

  // Calculate separation vector between two overlapping nodes
  const calculateSeparationVector = useCallback((bounds1, bounds2, minDistance) => {
    const centerDistance = Math.sqrt(
      Math.pow(bounds1.centerX - bounds2.centerX, 2) +
      Math.pow(bounds1.centerY - bounds2.centerY, 2)
    );

    // If centers are too close, use a default direction
    if (centerDistance < 5) {
      return {
        dx: minDistance,
        dy: 0
      };
    }

    // Calculate required separation distance
    const requiredDistance = (bounds1.width + bounds2.width) / 2 +
                            (bounds1.height + bounds2.height) / 2 +
                            minDistance;

    const overlap = requiredDistance - centerDistance;

    if (overlap <= 0) return { dx: 0, dy: 0 };

    // Normalize direction vector
    const directionX = (bounds1.centerX - bounds2.centerX) / centerDistance;
    const directionY = (bounds1.centerY - bounds2.centerY) / centerDistance;

    // Calculate how much each node should move (split the overlap)
    const moveDistance = overlap / 2;

    return {
      dx: directionX * moveDistance,
      dy: directionY * moveDistance
    };
  }, []);

  // Main collision resolution function
  const resolveCollisions = useCallback((nodes, updateNodePosition, scale = 1) => {
    if (!nodes || nodes.length < 2) return;

    const nodeData = [];
    const adjustments = new Map();

    // Collect node data and bounds
    for (const node of nodes) {
      const nodeRef = nodeRefs.current[node.id];
      if (!nodeRef) continue;

      const bounds = getNodeBounds(nodeRef, scale);
      if (!bounds) continue;

      nodeData.push({
        id: node.id,
        node,
        bounds,
        ref: nodeRef
      });

      adjustments.set(node.id, { dx: 0, dy: 0 });
    }

    // Check all pairs for collisions
    for (let i = 0; i < nodeData.length; i++) {
      for (let j = i + 1; j < nodeData.length; j++) {
        const nodeA = nodeData[i];
        const nodeB = nodeData[j];

        if (boundingBoxesOverlap(nodeA.bounds, nodeB.bounds, minDistance)) {
          const separation = calculateSeparationVector(
            nodeA.bounds,
            nodeB.bounds,
            minDistance
          );

          // Apply separation to both nodes
          const currentAdjustmentA = adjustments.get(nodeA.id);
          const currentAdjustmentB = adjustments.get(nodeB.id);

          adjustments.set(nodeA.id, {
            dx: currentAdjustmentA.dx + separation.dx,
            dy: currentAdjustmentA.dy + separation.dy
          });

          adjustments.set(nodeB.id, {
            dx: currentAdjustmentB.dx - separation.dx,
            dy: currentAdjustmentB.dy - separation.dy
          });
        }
      }
    }

    // Apply adjustments with animation
    const animationPromises = [];

    for (const [nodeId, adjustment] of adjustments) {
      if (Math.abs(adjustment.dx) > 1 || Math.abs(adjustment.dy) > 1) {
        const nodeData = nodes.find(n => n.id === nodeId);
        if (nodeData) {
          const newPosition = {
            x: nodeData.position.x + adjustment.dx,
            y: nodeData.position.y + adjustment.dy
          };

          // Enable animation for this update
          const promise = new Promise(resolve => {
            updateNodePosition(nodeId, newPosition, { animate: true });
            setTimeout(resolve, animationDuration);
          });

          animationPromises.push(promise);
        }
      }
    }

    // Return promise that resolves when all animations complete
    return Promise.all(animationPromises);
  }, [
    nodeRefs,
    minDistance,
    animationDuration,
    getNodeBounds,
    boundingBoxesOverlap,
    calculateSeparationVector
  ]);

  // Wrapper function that works with the existing API
  const resolveCollisionsWrapper = useCallback((nodes, updateNodePosition, scale) => {
    return resolveCollisions(nodes, updateNodePosition, scale);
  }, [resolveCollisions]);

  return {
    resolveCollisions: resolveCollisionsWrapper,
    minDistance,
    animationDuration
  };
};